import {
    Button,
    CircularProgress,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { RefreshCw, RotateCcw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { vec3 } from 'gl-matrix';
import { cmToMm, formatMmAsCm } from '~/utils/units';
import { generateUniqueStructureNameFromList } from '~/domain/services/dicomProcessor';
import type { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { useMarginPreview } from './useMarginPreview';
import { useMarginApplication } from './useMarginApplication';
import SectionLabel from '~/components/SectionLabel';
import {
    ActionButtons,
    LabeledSelect,
    OutputStructureSelect,
    getCustomNameError,
    getStructureColor,
    outputActionLabel,
    outputTargetBlocker,
} from '../OperationFormControls';
import {
    NEW_OUTPUT_TARGET,
    OutputTarget,
    isOverwriteUnpicked,
    overwrittenRoiNumber,
} from '~/hooks/structure-set/outputTarget';
import SymmetricMarginControls from './SymmetricMarginControls';
import AsymmetricMarginControls, {
    AsymmetricMargins,
} from './AsymmetricMarginControls';
import { getDefaultMarginName } from './marginToolbarUtils';
import OperationContentShell, {
    OperationShellRenderProps,
} from '../OperationContentShell';
import { WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS } from '../autoPreview';
import { MAX_MARGIN_MM } from './marginRange';
import {
    asymmetricPreviewKey,
    IDENTITY_PREVIEW_KEY,
    symmetricPreviewKey,
} from './marginPreviewKey';
import { useReportBusy } from '~/components/toolbar/OperationsToolbar/useReportBusy';

type MarginMode = 'symmetric' | 'asymmetric';
type MarginDirection = 'inner' | 'outer';

interface MarginOptionsProps {
    marginType: MarginDirection;
    onMarginTypeChange: (d: MarginDirection) => void;
    currentMode: MarginMode;
    onModeChange: (m: MarginMode) => void;
    onReset: () => void;
}

function MarginOptions({
    marginType,
    onMarginTypeChange,
    currentMode,
    onModeChange,
    onReset,
}: MarginOptionsProps) {
    return (
        <Stack spacing={1}>
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <SectionLabel>Margin</SectionLabel>
                {currentMode === 'asymmetric' && (
                    <Button
                        onClick={onReset}
                        size="small"
                        variant="text"
                        startIcon={<RotateCcw size={12} />}
                        sx={{
                            textTransform: 'none',
                            fontSize: 11,
                            py: 0,
                            minWidth: 'auto',
                        }}
                    >
                        Reset
                    </Button>
                )}
            </Stack>
            <Stack direction="row" spacing={1}>
                <ToggleButtonGroup
                    value={marginType}
                    exclusive
                    onChange={(_, v) => v && onMarginTypeChange(v)}
                    size="small"
                    fullWidth
                >
                    <ToggleButton value="outer">Expand</ToggleButton>
                    <ToggleButton value="inner">Contract</ToggleButton>
                </ToggleButtonGroup>
                <ToggleButtonGroup
                    value={currentMode}
                    exclusive
                    onChange={(_, v) => v && onModeChange(v)}
                    size="small"
                    fullWidth
                >
                    <ToggleButton value="symmetric">Uniform</ToggleButton>
                    <ToggleButton value="asymmetric">Variable</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
        </Stack>
    );
}

/** The same margin in every direction, in cm. */
const uniformMargins = (marginCm: number): AsymmetricMargins => ({
    left: marginCm,
    right: marginCm,
    anterior: marginCm,
    posterior: marginCm,
    superior: marginCm,
    inferior: marginCm,
});

const INITIAL_ASYMMETRIC = uniformMargins(0);

/** Why the input structure can take no margin, or undefined when it can. */
function inputBlockedReason(inputRoi: RegionOfInterest): string | undefined {
    if (inputRoi.isPointRoi) {
        return 'Selected input structure is a point. Margin cannot be applied.';
    }
    if (inputRoi.isEmpty) {
        return 'Selected input structure is empty. Margin cannot be applied.';
    }
    return undefined;
}

interface ApplyGates {
    inputBlocker: string | undefined;
    customNameError: string | null;
    /** Overwrite is chosen and no structure is picked. */
    targetUnpicked: string | undefined;
    isMarginInvalid: boolean;
    isApplying: boolean;
    isCalculatingPreview: boolean;
    isPreviewCurrent: boolean;
}

/**
 * Why Apply is unavailable, or undefined when nothing holds it. The one
 * source of both the disabled state and its hint, so Apply is never held
 * without a reason in reach.
 */
function applyDisabledReason({
    inputBlocker,
    customNameError,
    targetUnpicked,
    isMarginInvalid,
    isApplying,
    isCalculatingPreview,
    isPreviewCurrent,
}: ApplyGates): string | undefined {
    if (inputBlocker !== undefined) {
        return inputBlocker;
    }
    if (customNameError !== null) {
        return customNameError;
    }
    if (targetUnpicked !== undefined) {
        return targetUnpicked;
    }
    if (isMarginInvalid) {
        return `Enter a margin between 0.00 and ${formatMmAsCm(MAX_MARGIN_MM)} cm.`;
    }
    if (isApplying) {
        return 'Applying…';
    }
    if (isCalculatingPreview) {
        return 'Calculating preview…';
    }
    if (!isPreviewCurrent) {
        return 'No preview to apply.';
    }
    return undefined;
}

interface MarginOperationContentProps {
    mode: MarginMode;
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

export default function MarginOperationContent({
    mode,
    onClose,
    onPreventCloseChange,
}: MarginOperationContentProps) {
    return (
        <OperationContentShell>
            {(shellProps) => (
                <MarginFormContent
                    key={mode}
                    mode={mode}
                    onClose={onClose}
                    onPreventCloseChange={onPreventCloseChange}
                    {...shellProps}
                />
            )}
        </OperationContentShell>
    );
}

// Inner component that has access to a valid inputRoi —
// hooks need stable references and can't be called conditionally.
interface MarginFormContentProps extends OperationShellRenderProps {
    mode: 'symmetric' | 'asymmetric';
    onClose: () => void;
    onPreventCloseChange: (prevented: boolean) => void;
}

function MarginFormContent({
    mode,
    onClose,
    onPreventCloseChange,
    environment,
    inputRoi,
    structures,
    onInputROIChange,
}: MarginFormContentProps) {
    const intl = useIntl();
    // Form state
    const [currentMode, setCurrentMode] = useState<MarginMode>(mode);
    const [marginType, setMarginType] = useState<MarginDirection>('outer');
    const [margin, setMargin] = useState(0.5);
    const [asymmetric, setAsymmetric] =
        useState<AsymmetricMargins>(INITIAL_ASYMMETRIC);
    // Cleared as soon as the user sets a per-direction margin themselves,
    // so seeding never rewrites values they put there.
    const [canSeedAsymmetric, setCanSeedAsymmetric] = useState(true);
    // Margin fields showing text that could not be committed: blank, not a
    // number, or out of range. The committed margins, and so the preview,
    // then lag what the user reads, so Apply waits until every field agrees.
    const [invalidMarginFields, setInvalidMarginFields] = useState<
        ReadonlySet<string>
    >(new Set());
    const [outputTarget, setOutputTarget] =
        useState<OutputTarget>(NEW_OUTPUT_TARGET);
    const outputRoiNumber = overwrittenRoiNumber(outputTarget);
    const [customRoiName, setCustomRoiName] = useState('');

    // Preview and application hooks.
    // Destructure the stable useCallback references so downstream deps
    // don't re-fire on every isPreviewing state change.
    const {
        isPreviewing,
        handlePreviewSymmetric,
        handlePreviewAsymmetric,
        handlePreviewIdentity,
        consumeSymmetricPreview,
        consumeAsymmetricPreview,
        getExpanded,
        abortInFlight: abortPreviewInFlight,
        cleanup: previewCleanup,
        updateView,
        source,
        heldKey,
        failedKey,
    } = useMarginPreview({ environment, inputRoi });

    // True during the debounce window, before the compute itself starts.
    const [isDebouncing, setIsDebouncing] = useState(false);
    // Bumped to ask again for the preview of margins that have not changed,
    // after their run failed.
    const [previewRetry, setPreviewRetry] = useState(0);

    const cleanupRef = useRef(previewCleanup);
    cleanupRef.current = previewCleanup;

    const handleClose = useCallback(() => {
        previewCleanup();
        onClose();
    }, [previewCleanup, onClose]);

    const apply = useMarginApplication({
        environment,
        inputRoi,
        onSuccess: handleClose,
        updateView,
        consumeSymmetricPreview,
        consumeAsymmetricPreview,
        getExpanded,
    });

    // Flow isPreviewing state to parent for close-blocking
    const isPreventing = isPreviewing || apply.isProcessing;
    useReportBusy(isPreventing, onPreventCloseChange);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            cleanupRef.current();
        };
    }, []);

    // Computed values
    const currentInputName = inputRoi.name;
    const inputBlocker = inputBlockedReason(inputRoi);

    const inputColor = getStructureColor(inputRoi.colorRgb8);

    const customNameError = useMemo(
        () =>
            getCustomNameError(
                customRoiName,
                structures.map((s) => s.name)
            ),
        [customRoiName, structures]
    );

    const structureOptions = useMemo(
        () =>
            structures.map((s) => ({
                roiNumber: s.roiNumber,
                label: s.name,
                color: getStructureColor(s.color),
            })),
        [structures]
    );

    const getActual = useCallback(
        (v: number) => (marginType === 'inner' ? -v : v),
        [marginType]
    );

    // Compute margins in LPS orientation for SDFOperations:
    // negLps = [Right (-X), Anterior (-Y), Inferior (-Z)]
    // posLps = [Left (+X), Posterior (+Y), Superior (+Z)]
    const computeVec3s = useCallback(() => {
        const { left, right, anterior, posterior, superior, inferior } =
            asymmetric;
        return {
            negLpsMm: vec3.fromValues(
                cmToMm(getActual(right)),
                cmToMm(getActual(anterior)),
                cmToMm(getActual(inferior))
            ),
            posLpsMm: vec3.fromValues(
                cmToMm(getActual(left)),
                cmToMm(getActual(posterior)),
                cmToMm(getActual(superior))
            ),
        };
    }, [asymmetric, getActual]);

    const existingNames = useMemo(
        () => structures.map((s) => s.name),
        [structures]
    );

    // The name a new structure gets when the field is left empty, and
    // what the field shows as its placeholder.
    const defaultOutputName = useMemo(
        () =>
            generateUniqueStructureNameFromList(
                existingNames,
                getDefaultMarginName(currentInputName)
            ),
        [existingNames, currentInputName]
    );
    // The name a new structure gets: the field's, made unique, or the
    // placeholder's.
    const newStructureName = customRoiName.trim()
        ? generateUniqueStructureNameFromList(
              existingNames,
              customRoiName.trim()
          )
        : defaultOutputName;
    const actionLabel = outputActionLabel(
        intl,
        outputTarget,
        structures,
        newStructureName
    );

    const handleApply = useCallback(() => {
        if (isOverwriteUnpicked(outputTarget)) {
            return;
        }
        const newName =
            outputTarget.kind === 'new' ? newStructureName : undefined;

        if (currentMode === 'symmetric') {
            apply.handleSymmetricApply(
                cmToMm(getActual(margin)),
                outputRoiNumber,
                newName
            );
        } else {
            const { negLpsMm, posLpsMm } = computeVec3s();
            apply.handleAsymmetricApply(
                negLpsMm,
                posLpsMm,
                outputRoiNumber,
                newName
            );
        }
    }, [
        outputTarget,
        outputRoiNumber,
        newStructureName,
        currentMode,
        margin,
        getActual,
        computeVec3s,
        apply,
    ]);

    // Clearing the directions is a choice like any other: the zeros stay
    // until the user changes them.
    const handleResetDirections = useCallback(() => {
        setAsymmetric(INITIAL_ASYMMETRIC);
        setCanSeedAsymmetric(false);
    }, []);

    // Opening the per-direction fields starts them from the uniform margin, so
    // only the directions that differ need editing. The preview is recomputed
    // by the per-direction pass (a dilation, not the uniform level-set shift),
    // so equal margins need not give an identical surface.
    const handleModeChange = useCallback(
        (nextMode: MarginMode) => {
            if (nextMode === 'asymmetric' && canSeedAsymmetric) {
                setAsymmetric(uniformMargins(margin));
            }
            // The other mode's fields unmount without reporting back.
            setInvalidMarginFields(new Set());
            setCurrentMode(nextMode);
        },
        [canSeedAsymmetric, margin]
    );

    const setMarginFieldValidity = useCallback(
        (field: string, isValid: boolean) => {
            setInvalidMarginFields((prev) => {
                if (prev.has(field) !== isValid) {
                    return prev;
                }
                const next = new Set(prev);
                if (isValid) {
                    next.delete(field);
                } else {
                    next.add(field);
                }
                return next;
            });
        },
        []
    );

    const allZero =
        currentMode === 'symmetric'
            ? margin === 0
            : Object.values(asymmetric).every((v) => v === 0);

    // Key of the preview the current margins around the current source
    // publish; compared against the held one so Apply only ever hands off
    // a preview of these margins around this structure as it is now.
    const currentPreviewKey = useMemo(() => {
        if (allZero) {
            return IDENTITY_PREVIEW_KEY;
        }
        if (currentMode === 'symmetric') {
            return symmetricPreviewKey(source, cmToMm(getActual(margin)));
        }
        const { negLpsMm, posLpsMm } = computeVec3s();
        return asymmetricPreviewKey(source, negLpsMm, posLpsMm);
    }, [allZero, currentMode, source, margin, getActual, computeVec3s]);

    // Auto-trigger preview with debounce when the margin values or the
    // source change, or when a retry is asked for. In-flight computes are
    // aborted on the next change, so edits made within the window cost
    // one compute rather than one each.
    useEffect(() => {
        if (apply.isProcessing) {
            // Abort any in-flight preview compute so its result doesn't
            // land on the slot mid-apply. Held results stay so apply
            // can still consume them.
            abortPreviewInFlight();
            setIsDebouncing(false);
            return;
        }

        if (inputBlocker !== undefined) {
            // No input geometry: nothing to preview, restore full visibility.
            previewCleanup();
            setIsDebouncing(false);
            return;
        }

        if (allZero) {
            // No margin to compute, but still "set the stage": dim the other
            // structures and highlight the source via an identity preview,
            // rather than showing nothing with everything at full visibility.
            handlePreviewIdentity();
            setIsDebouncing(false);
            return;
        }

        // A non-zero margin is pending. Abort any in-flight compute, but KEEP
        // the currently-displayed preview (and the dimmed-others mode) on
        // screen until the replacement is ready. Calling previewCleanup() here
        // would clear operationPreviewStructure for the whole debounce +
        // recompute window, un-dimming the other structures — the flicker.
        abortPreviewInFlight();
        setIsDebouncing(true);

        const timeout = setTimeout(() => {
            setIsDebouncing(false);
            if (currentMode === 'symmetric') {
                handlePreviewSymmetric(cmToMm(getActual(margin)));
            } else {
                const { negLpsMm, posLpsMm } = computeVec3s();
                handlePreviewAsymmetric(negLpsMm, posLpsMm);
            }
        }, WHOLE_VOLUME_AUTO_PREVIEW_DELAY_MS);

        return () => {
            clearTimeout(timeout);
        };
    }, [
        margin,
        asymmetric,
        allZero,
        marginType,
        currentMode,
        inputBlocker,
        source,
        getActual,
        computeVec3s,
        handlePreviewSymmetric,
        handlePreviewAsymmetric,
        handlePreviewIdentity,
        previewCleanup,
        abortPreviewInFlight,
        apply.isProcessing,
        previewRetry,
    ]);

    const retryPreview = useCallback(() => {
        setPreviewRetry((n) => n + 1);
    }, []);

    const updateAsymmetric = useCallback(
        (direction: keyof AsymmetricMargins, value: number) => {
            setCanSeedAsymmetric(false);
            setAsymmetric((prev) => ({ ...prev, [direction]: value }));
        },
        []
    );

    const setUniformValidity = useCallback(
        (isValid: boolean) => setMarginFieldValidity('uniform', isValid),
        [setMarginFieldValidity]
    );

    // customNameError only blocks Apply when the user is creating a new
    // structure. Switching to an existing output while a stale custom name
    // is in the text box must not keep Apply disabled.
    const customNameBlocker =
        outputTarget.kind === 'new' ? customNameError : null;
    const isMarginInvalid = invalidMarginFields.size > 0;

    // Apply hands off the preview on screen, so it waits for the one the
    // current margins produce: never a superseded run's, never a recompute
    // the user has not seen. The button carries the progress state, and
    // becomes the retry once a run has failed, so the panel keeps its
    // height.
    const isCalculatingPreview = isPreviewing || isDebouncing;
    const isPreviewCurrent = heldKey !== null && heldKey === currentPreviewKey;
    const isPreviewFailed =
        !isCalculatingPreview &&
        inputBlocker === undefined &&
        failedKey === currentPreviewKey;
    const applyBlocker = applyDisabledReason({
        inputBlocker,
        customNameError: customNameBlocker,
        targetUnpicked: outputTargetBlocker(intl, outputTarget),
        isMarginInvalid,
        isApplying: apply.isProcessing,
        isCalculatingPreview,
        isPreviewCurrent,
    });

    return (
        <>
            <LabeledSelect
                label="Input Structure"
                value={inputRoi.roiNumber}
                onChange={onInputROIChange}
                options={structureOptions}
            />

            <MarginOptions
                marginType={marginType}
                onMarginTypeChange={setMarginType}
                currentMode={currentMode}
                onModeChange={handleModeChange}
                onReset={handleResetDirections}
            />

            {currentMode === 'symmetric' ? (
                <SymmetricMarginControls
                    margin={margin}
                    onMarginChange={setMargin}
                    onValidityChange={setUniformValidity}
                />
            ) : (
                <AsymmetricMarginControls
                    margins={asymmetric}
                    onMarginChange={updateAsymmetric}
                    onValidityChange={setMarginFieldValidity}
                />
            )}

            <Divider />

            <OutputStructureSelect
                target={outputTarget}
                onTargetChange={setOutputTarget}
                inputColor={inputColor}
                inputRoiNumber={inputRoi.roiNumber}
                availableStructures={structures}
                customRoiName={customRoiName}
                setCustomRoiName={setCustomRoiName}
                customNameError={customNameError}
                defaultName={defaultOutputName}
            />

            {inputBlocker !== undefined && (
                <Typography
                    fontSize={10}
                    color="warning.main"
                    fontStyle="italic"
                >
                    {inputBlocker}
                </Typography>
            )}

            {inputRoi.volumeCm3 !== undefined && inputRoi.volumeCm3 > 100 && (
                <Typography
                    fontSize={10}
                    color="text.secondary"
                    fontStyle="italic"
                >
                    Preview may take longer for large structures (
                    {inputRoi.volumeCm3.toFixed(1)} cm³)
                </Typography>
            )}

            {isPreviewFailed ? (
                <ActionButtons
                    onApply={retryPreview}
                    label="Retry preview"
                    startIcon={<RefreshCw size={14} />}
                />
            ) : (
                <ActionButtons
                    onApply={handleApply}
                    label={actionLabel}
                    isApplyDisabled={applyBlocker !== undefined}
                    applyDisabledReason={applyBlocker}
                    startIcon={
                        isCalculatingPreview ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : undefined
                    }
                />
            )}
        </>
    );
}
